import { useEffect, useRef, useState } from 'react';

/**
 * Tracks whether an element is currently intersecting the viewport at the
 * given threshold. Continuous (not once-only) so callers can pause work -
 * e.g. an ad-poster cycle - while the element is off-screen, and resume it
 * when it scrolls back in.
 */
export function useInView<T extends HTMLElement>(threshold = 0.4) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((entry) => setInView(entry.isIntersecting)),
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return { ref, inView } as const;
}

/** True while the document tab is hidden (backgrounded/minimized). */
export function useTabHidden(): boolean {
  const [hidden, setHidden] = useState(
    typeof document !== 'undefined' ? document.hidden : false
  );
  useEffect(() => {
    const onChange = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return hidden;
}
